<div [class]="embedded() ? 'w-full' : 'w-full max-w-6xl mx-auto p-6'">
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-red-600"></i>
      <p class="text-red-800">{{ error() }}</p>
    </div>
  }

  <div class="relative">
    @if (formDataLoading()) {
      <div
        class="absolute inset-0 z-40 flex items-start justify-center rounded-xl bg-slate-400/25 backdrop-blur-[2px] pt-24"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="flex flex-col items-center gap-3 rounded-2xl border border-slate-300/80 bg-white/95 px-8 py-6 shadow-lg">
          <i class="pi pi-spin pi-spinner text-3xl text-sky-600"></i>
          <p class="text-sm font-semibold text-slate-800">Cargando producto…</p>
        </div>
      </div>
    }

  <form [formGroup]="productForm" (ngSubmit)="onSubmit()">
    <p-card styleClass="sale-form-card">
      <ng-template pTemplate="header">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3 min-w-0">
              @if (!embedded()) {
                <p-button
                  icon="pi pi-arrow-left"
                  [rounded]="true"
                  [text]="true"
                  severity="secondary"
                  type="button"
                  (onClick)="cancel()"
                  pTooltip="Volver al listado"
                  tooltipPosition="bottom"
                />
              }
              <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
                <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <i class="pi pi-box"></i>
                </span>
                <span class="truncate">{{ isEditMode() ? 'Editar producto' : 'Nuevo producto' }}</span>
              </h2>
            </div>
            @if (editingProductId(); as productId) {
              <p-button
                icon="pi pi-refresh"
                [rounded]="true"
                [text]="true"
                severity="secondary"
                type="button"
                (onClick)="refreshProduct()"
                [loading]="loading()"
                [disabled]="loading()"
                pTooltip="Actualizar producto"
                tooltipPosition="bottom"
              />
            }
          </div>
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-2">
          <div class="inline-flex items-baseline gap-x-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
            <span class="text-[11px] font-semibold tracking-wider text-slate-500">STATUS:</span>
            <span class="text-sm font-medium text-slate-900 font-mono">{{ statusDisplayLabel() }}</span>
            <i
              class="pi pi-question-circle text-slate-400 cursor-help text-xs shrink-0"
              [pTooltip]="statusTooltip"
              tooltipPosition="bottom"
            ></i>
          </div>
        </div>
      </ng-template>
      <p-tabs [value]="activeTab()" (valueChange)="activeTab.set($event)">
        <p-tablist>
          @for (tab of tabs; track tab.value) {
            <p-tab
              [value]="tab.value"
              [disabled]="tab.value === 'auditoria' && !isEditMode()"
              class="flex items-center gap-2! text-inherit"
            >
              <i [class]="tab.icon"></i>
              <span>{{ tab.label }}</span>
            </p-tab>
          }
        </p-tablist>
        <p-tabpanels>
          <!-- Tab General -->
          <p-tabpanel value="general">
            <div class="mt-4 mb-4">
              <h3 class="text-lg font-semibold text-slate-900">Datos generales</h3>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <input pInputText
                    id="name"
                    formControlName="name"
                    [maxlength]="255"
                    fluid
                    autocomplete="off"
                    [pTooltip]="getNameTooltip()"
                    tooltipPosition="top"
                    appendTo="body"
                    [tooltipDisabled]="!getNameTooltip()"
                    [showDelay]="200"
                    [invalid]="(productForm.get('name')?.invalid ?? false) && (productForm.get('name')?.touched ?? false)"
                  />
                  <label for="name">Nombre <span class="text-red-500">*</span></label>
                </p-floatlabel>
                <small class="text-slate-500">Nombre del producto para identificación y búsqueda.</small>
                @if (productForm.get('name')?.hasError('required') && productForm.get('name')?.touched) {
                  <small class="text-red-600">El nombre es obligatorio.</small>
                }
                @if (productForm.get('name')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo 255 caracteres.</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-inputNumber
                    id="unitPrice"
                    formControlName="unitPrice"
                    [min]="0"
                    mode="decimal"
                    locale="en-US"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="4"
                    [useGrouping]="true"
                    fluid
                    class="[&_input]:text-right"
                    [pTooltip]="getUnitPriceTooltip()"
                    tooltipPosition="top"
                    appendTo="body"
                    [tooltipDisabled]="!getUnitPriceTooltip()"
                    [showDelay]="200"
                    [invalid]="(productForm.get('unitPrice')?.invalid ?? false) && (productForm.get('unitPrice')?.touched ?? false)"
                  />
                  <label for="unitPrice">Precio unitario <span class="text-red-500">*</span></label>
                </p-floatlabel>
                <small class="text-slate-500">Precio unitario o por pieza del producto.</small>
                @if (productForm.get('unitPrice')?.hasError('required') && productForm.get('unitPrice')?.touched) {
                  <small class="text-red-600">El precio es obligatorio.</small>
                }
                @if (productForm.get('unitPrice')?.hasError('min')) {
                  <small class="text-red-600">El precio debe ser mayor o igual a 0.</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-select
                    formControlName="currencyId"
                    inputId="currencyId"
                    [options]="currencySelectOptions"
                    optionLabel="label"
                    optionValue="id"
                    class="w-full"
                    filter
                    [pTooltip]="getCurrencyTooltip()"
                    tooltipPosition="top"
                    appendTo="body"
                    [tooltipDisabled]="!getCurrencyTooltip()"
                    [showDelay]="200"
                    [invalid]="(productForm.get('currencyId')?.invalid ?? false) && (productForm.get('currencyId')?.touched ?? false)"
                  >
                    <ng-template #item let-option>
                      <span class="block truncate" [pTooltip]="option.label" tooltipPosition="right" appendTo="body">{{ option.label }}</span>
                    </ng-template>
                  </p-select>
                  <label for="currencyId">Moneda <span class="text-red-500">*</span></label>
                </p-floatlabel>
                <small class="text-slate-500">Divisa en la que se cobra el producto.</small>
                @if (productForm.get('currencyId')?.hasError('required') && productForm.get('currencyId')?.touched) {
                  <small class="text-red-600">La moneda es obligatoria.</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <input pInputText
                    id="sku"
                    formControlName="sku"
                    [maxlength]="100"
                    fluid
                    autocomplete="off"
                    [pTooltip]="getSkuTooltip()"
                    tooltipPosition="top"
                    appendTo="body"
                    [tooltipDisabled]="!getSkuTooltip()"
                    [showDelay]="200"
                  />
                  <label for="sku">SKU</label>
                </p-floatlabel>
                <small class="text-slate-500">Identificador único del producto.</small>
                @if (productForm.get('sku')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo 100 caracteres.</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <input pInputText
                    id="externalId"
                    formControlName="externalId"
                    [maxlength]="100"
                    fluid
                    autocomplete="off"
                    [pTooltip]="getExternalIdTooltip()"
                    tooltipPosition="top"
                    appendTo="body"
                    [tooltipDisabled]="!getExternalIdTooltip()"
                    [showDelay]="200"
                  />
                  <label for="externalId">ID externo</label>
                </p-floatlabel>
                <small class="text-slate-500">Identificador del sistema externo como CRM, ERP, etc.</small>
                @if (productForm.get('externalId')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo 100 caracteres.</small>
                }
              </div>

              <div class="flex flex-col gap-1 md:col-span-2">
                <p-floatlabel class="w-full" variant="on">
                  <textarea
                    pTextarea
                    id="description"
                    formControlName="description"
                    rows="3"
                    [maxlength]="1000"
                    fluid
                    autocomplete="off"
                    [pTooltip]="getDescriptionTooltip()"
                    tooltipPosition="top"
                    appendTo="body"
                    [tooltipDisabled]="!getDescriptionTooltip()"
                    [showDelay]="200"
                  ></textarea>
                  <label for="description">Descripción</label>
                </p-floatlabel>
                @if (productForm.get('description')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo 1000 caracteres.</small>
                }
                <small class="text-slate-500">Agrega una descripción para identificar el producto.</small>
              </div>
            </div>

            <div class="mt-8 border-t border-slate-200 pt-6">
              <div class="mb-4">
                <h3 class="text-lg font-semibold text-slate-900">Datos fiscales</h3>
                <p class="text-sm text-slate-500">Opcionales al guardar. Son necesarios para timbrar una factura.</p>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="taxInclusive"
                      inputId="taxInclusive"
                      [options]="taxInclusiveOptions"
                      optionLabel="label"
                      optionValue="value"
                      [showClear]="true"
                      class="w-full"
                      [pTooltip]="getTaxInclusiveTooltip()"
                      tooltipPosition="top"
                      appendTo="body"
                      [tooltipDisabled]="!getTaxInclusiveTooltip()"
                      [showDelay]="200"
                    />
                    <label for="taxInclusive">Impuesto incluido</label>
                  </p-floatlabel>
                  <small class="text-slate-500">¿El precio unitario incluye impuestos?</small>
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-autocomplete
                      id="measureUnit"
                      formControlName="measureUnitSelection"
                      [suggestions]="measureUnitOptions()"
                      (completeMethod)="onMeasureUnitComplete($event)"
                      (onSelect)="onMeasureUnitSelected($event.value)"
                      (onBlur)="onMeasureUnitBlur()"
                      (onClear)="onMeasureUnitCleared()"
                      [forceSelection]="false"
                      optionLabel="displayLabel"
                      fluid
                      [pTooltip]="getMeasureUnitTooltip()"
                      tooltipPosition="top"
                      appendTo="body"
                      [tooltipDisabled]="!getMeasureUnitTooltip()"
                      [showDelay]="200"
                      [invalid]="(productForm.get('measureUnitSelection')?.invalid ?? false) && (productForm.get('measureUnitSelection')?.touched ?? false)"
                    >
                      <ng-template let-item #item>
                        <span class="block truncate" [pTooltip]="displayMeasureUnit(item)" tooltipPosition="right" appendTo="body">
                          {{ displayMeasureUnit(item) }}
                        </span>
                      </ng-template>
                    </p-autocomplete>
                    <label for="measureUnit">Unidad de medida</label>
                  </p-floatlabel>
                  <small class="text-slate-500">Define cómo se mide el producto.</small>
                  @if (productForm.get('measureUnitSelection')?.hasError('incompleteSelection') && productForm.get('measureUnitSelection')?.touched) {
                    <small class="text-red-600">Selecciona una unidad de medida de la lista.</small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-autocomplete
                      id="taxCode"
                      formControlName="taxCodeSelection"
                      [suggestions]="taxCodeOptions()"
                      (completeMethod)="onTaxCodeComplete($event)"
                      (onSelect)="onTaxCodeSelected($event.value)"
                      (onBlur)="onTaxCodeBlur()"
                      (onClear)="onTaxCodeCleared()"
                      [forceSelection]="false"
                      optionLabel="displayLabel"
                      fluid
                      [pTooltip]="getTaxCodeTooltip()"
                      tooltipPosition="top"
                      appendTo="body"
                      [tooltipDisabled]="!getTaxCodeTooltip()"
                      [showDelay]="200"
                      [invalid]="(productForm.get('taxCodeSelection')?.invalid ?? false) && (productForm.get('taxCodeSelection')?.touched ?? false)"
                    >
                      <ng-template let-item #item>
                        <span class="block truncate" [pTooltip]="displayTaxProductCode(item)" tooltipPosition="right" appendTo="body">
                          {{ displayTaxProductCode(item) }}
                        </span>
                      </ng-template>
                    </p-autocomplete>
                    <label for="taxCode">Código fiscal</label>
                  </p-floatlabel>
                  <small class="text-slate-500">Clave SAT para clasificación fiscal.</small>
                  @if (productForm.get('taxCodeSelection')?.hasError('incompleteSelection') && productForm.get('taxCodeSelection')?.touched) {
                    <small class="text-red-600">Selecciona un código fiscal de la lista.</small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="satObjetoImpId"
                      inputId="satObjetoImpId"
                      [options]="satObjetoImpOptions()"
                      optionLabel="description"
                      optionValue="id"
                      [showClear]="true"
                      class="w-full"
                      filter
                      [pTooltip]="getSatObjetoImpTooltip()"
                      tooltipPosition="top"
                      appendTo="body"
                      [tooltipDisabled]="!getSatObjetoImpTooltip()"
                      [showDelay]="200"
                    >
                      <ng-template #item let-option>
                        <span class="block truncate" [pTooltip]="option.description" tooltipPosition="right" appendTo="body">{{ option.description }}</span>
                      </ng-template>
                    </p-select>
                    <label for="satObjetoImpId">Objeto de impuesto SAT</label>
                  </p-floatlabel>
                  <small class="text-slate-500">Catálogo SAT de objeto de impuesto.</small>
                </div>
              </div>
            </div>

            <!-- Impuestos -->
            <div class="mt-8 border-t border-slate-200 pt-6">
              <div class="mb-4">
                <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <i class="pi pi-percentage"></i>
                  Impuestos del producto
                </h3>
                <p class="text-sm text-slate-500">
                  Marca los impuestos del catálogo que aplican a este producto. Los cambios se guardan al pulsar Guardar cambios.
                </p>
              </div>

              @if (productTaxLoading()) {
                <p class="text-sm text-slate-500 py-6 text-center">Cargando impuestos...</p>
              } @else if (productTaxOptions().length === 0) {
                <p class="text-sm text-slate-500 py-6 text-center">No hay impuestos en el catálogo.</p>
              } @else {
                <div class="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden mb-6">
                  <div class="overflow-x-auto">
                    <table class="w-full">
                      <thead class="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th class="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase w-12"></th>
                          <th class="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase">Impuesto</th>
                          <th class="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase">Tasa</th>
                          <th class="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase">Monto</th>
                          <th class="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase">Aplicación</th>
                          <th class="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase">Cobro</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-slate-100">
                        @for (item of productTaxOptions(); track item.id) {
                          <tr class="hover:bg-slate-50 transition-colors">
                            <td class="px-4 py-3">
                              <p-checkbox
                                [binary]="true"
                                [ngModel]="isProductTaxSelected(item.id)"
                                (ngModelChange)="toggleProductTax(item.id, $event)"
                                [ngModelOptions]="{ standalone: true }"
                                [inputId]="'product-tax-' + item.id"
                              />
                            </td>
                            <td class="px-4 py-3 text-sm text-slate-900">
                              <label [for]="'product-tax-' + item.id" class="cursor-pointer">
                                <div class="font-medium">{{ item.tax?.displayName || item.tax?.taxKey || '—' }}</div>
                                @if (item.tax?.description) {
                                  <div class="text-xs text-slate-500">{{ item.tax.description }}</div>
                                }
                              </label>
                            </td>
                            <td class="px-4 py-3 text-sm text-slate-600 text-right tabular-nums">{{ item.taxRate != null ? (item.taxRate | number:'1.0-6') + '%' : '—' }}</td>
                            <td class="px-4 py-3 text-sm text-slate-600 text-right tabular-nums">{{ item.taxAmount != null ? (item.taxAmount | number:'1.2-4') : '—' }}</td>
                            <td class="px-4 py-3 text-sm text-slate-600">{{ getTaxApplicationTypeLabel(item.applicationType ?? '') || '—' }}</td>
                            <td class="px-4 py-3 text-sm text-slate-600">{{ getTaxCollectionTypeLabel(item.collectionType ?? '') || '—' }}</td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              }

              <div class="p-4 bg-slate-50 rounded-lg border border-slate-200 max-w-sm ml-auto">
                <h4 class="text-sm font-semibold text-slate-700 mb-3">Totales (preview por unidad)</h4>
                <div class="space-y-2 text-sm">
                  <div class="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>{{ productSubtotal | number:'1.2-4' }} {{ currencyCode }}</span>
                  </div>
                  <div class="flex justify-between text-slate-600">
                    <span class="pl-2">Traslados</span>
                    <span>{{ trasladosAmount | number:'1.2-4' }} {{ currencyCode }}</span>
                  </div>
                  <div class="flex justify-between text-slate-600">
                    <span class="pl-2">Retenciones</span>
                    <span>- {{ retencionesAmount | number:'1.2-4' }} {{ currencyCode }}</span>
                  </div>
                  <div class="flex justify-between font-semibold text-slate-900 pt-2 border-t border-slate-200">
                    <span>Total</span>
                    <span>{{ productTotal | number:'1.2-4' }} {{ currencyCode }}</span>
                  </div>
                </div>
              </div>
            </div>
          </p-tabpanel>

          <p-tabpanel value="auditoria">
            @if (activeTab() === 'auditoria') {
              <app-entity-audit-panel
                entityType="Product"
                [entityId]="editingProductId()"
                entityLabel="producto"
              />
            }
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <ng-template pTemplate="footer">
        <div class="!mt-4 flex flex-wrap items-center justify-end gap-4 mt-1 !p-4 !-m-5 bg-gray-50 p-2 border-t border-slate-200">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancel()" type="button" />
          @if (activeTab() === 'general') {
            <p-button
              [label]="isEditMode() ? 'Guardar cambios' : 'Crear producto'"
              icon="pi pi-check"
              type="submit"
              [loading]="loading()"
              [disabled]="productForm.invalid || loading()"
            />
          }
        </div>
      </ng-template>
    </p-card>
  </form>
  </div>
</div>
